/**
 * Colour primitives and the semantic layer built on top of them.
 *
 * Screens should only ever reference the *semantic* names (`surface.raised`,
 * `content.muted`). Primitives exist so the semantic layer has somewhere to
 * point; reaching past the semantic layer into `neutral[700]` from a component
 * is how a design system stops being one.
 */

export const primitives = {
  neutral: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    // Between-steps greys for the quietest text: the nearest steps either side
    // fail WCAG AA (4.5:1) on some surface, or match `content-muted`.
    450: '#8a97ab',
    500: '#64748b',
    550: '#5b6678',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    // Halfway between 800 and 900: a shaded row on a dark raised surface.
    850: '#172033',
    // Between 850 and 900: a dark row one step above the raised surface.
    875: '#131b2d',
    900: '#0f172a',
    950: '#020617',
  },
  brand: {
    50: '#eef4ff',
    100: '#dae6ff',
    200: '#bdd3ff',
    300: '#90b6ff',
    400: '#5b8dff',
    500: '#3563eb',
    600: '#2549c8',
    700: '#1d3aa1',
    800: '#1c3383',
    900: '#1c2f68',
    950: '#131d3f',
  },
  green: { 100: '#dcfce7', 500: '#22c55e', 600: '#16a34a', 700: '#15803d', 900: '#14532d' },
  amber: { 100: '#fef3c7', 500: '#f59e0b', 600: '#d97706', 900: '#78350f' },
  red: { 100: '#fee2e2', 500: '#ef4444', 600: '#dc2626', 900: '#7f1d1d' },
  blue: { 100: '#dbeafe', 500: '#3b82f6', 600: '#2563eb', 900: '#1e3a8a' },
  violet: {
    50: '#f5f3ff',
    100: '#ede9fe',
    200: '#ddd6fe',
    500: '#8b5cf6',
    600: '#7c3aed',
    700: '#6d28d9',
    900: '#4c1d95',
    950: '#2e1065',
  },
  pink: { 100: '#fce7f3', 500: '#ec4899', 600: '#db2777', 900: '#831843' },
  teal: { 100: '#ccfbf1', 500: '#14b8a6', 600: '#0d9488', 900: '#134e4a' },
} as const

/** The default light theme. Every other theme defines exactly these keys. */
const light = {
  'surface-base': primitives.neutral[50],
  'surface-raised': '#ffffff',
  'surface-sunken': primitives.neutral[100],
  'surface-overlay': '#ffffff',
  'surface-inverted': primitives.neutral[900],
  // A list's own title bar (Running order, Teams): set apart from its rows
  // and from the accent section headers inside it.
  'surface-heading': primitives.neutral[200],
  // A section header inside a list (ListHeader: WORSHIP, a team): a light
  // grey band, a step under the list's own title bar.
  'surface-section': primitives.neutral[100],
  // A level between a list's sections and its rows (a team's positions).
  'surface-subheading': primitives.neutral[50],
  // The phone's bottom tab bar: white on a light theme, black on a dark one.
  'surface-tab-bar': '#ffffff',

  'content-default': primitives.neutral[900],
  'content-muted': primitives.neutral[600],
  'content-subtle': primitives.neutral[550],
  'content-inverted': primitives.neutral[50],
  'content-brand': primitives.brand[600],
  // What is typed into an input, a text area or a select: its own colour, so
  // a field's words can be tuned apart from the page's (the design editor).
  'input-text': primitives.neutral[900],

  'border-subtle': primitives.neutral[200],
  'border-default': primitives.neutral[300],
  'border-strong': primitives.neutral[400],
  'border-brand': primitives.brand[500],

  'brand-solid': primitives.brand[500],
  'brand-solid-hover': primitives.brand[600],
  'brand-soft': primitives.brand[50],
  'brand-on-solid': '#ffffff',

  // Green 700, not 600: a solid carries a label (Accept), and white on 600 is 3.3:1.
  'success-solid': primitives.green[700],
  'success-soft': primitives.green[100],
  'success-content': primitives.green[900],
  // A label on the solid (an Accept button, a done step): white in a light theme.
  'success-on-solid': '#ffffff',
  'warning-solid': primitives.amber[600],
  'warning-soft': primitives.amber[100],
  'warning-content': primitives.amber[900],
  'danger-solid': primitives.red[600],
  'danger-soft': primitives.red[100],
  'danger-content': primitives.red[900],
  'danger-on-solid': '#ffffff',
  'info-solid': primitives.blue[600],
  'info-soft': primitives.blue[100],
  'info-content': primitives.blue[900],

  // Accent: what sets a list's section headers apart from its rows (a
  // plan's WORSHIP above its songs) — never a status, never the brand.
  'accent-solid': primitives.violet[600],
  'accent-soft': primitives.violet[50],
  'accent-content': primitives.violet[700],
  // Tag colours the semantic set has no name for.
  'violet-soft': primitives.violet[100],
  'violet-content': primitives.violet[900],
  'pink-soft': primitives.pink[100],
  'pink-content': primitives.pink[900],
  'teal-soft': primitives.teal[100],
  'teal-content': primitives.teal[900],
  // A song's arrangements, told apart at a glance: pastels in a fixed order
  // (green, yellow, orange…; \`arrangementSwatch\` in @church/domain). The same
  // in every theme, as a tag colour is.
  'swatch-green': '#a7e3b5',
  'swatch-yellow': '#f6dc7a',
  'swatch-orange': '#f9b98a',
  'swatch-red': '#f4a3a3',
  'swatch-pink': '#f2a7d0',
  'swatch-violet': '#c3b3f5',
  'swatch-blue': '#9cc4f5',
  'swatch-teal': '#8fdcd0',
  // Words on a swatch (a day's chip): dark on the pastel, in every theme.
  'swatch-content': primitives.neutral[800],
} as const

/** The default dark theme. */
const dark: Record<keyof typeof light, string> = {
  'surface-base': primitives.neutral[950],
  'surface-raised': primitives.neutral[900],
  'surface-sunken': '#000000',
  'surface-overlay': primitives.neutral[800],
  'surface-inverted': primitives.neutral[50],
  'surface-heading': primitives.neutral[800],
  'surface-section': primitives.neutral[850],
  'surface-subheading': primitives.neutral[875],
  'surface-tab-bar': '#000000',

  'content-default': primitives.neutral[100],
  'content-muted': primitives.neutral[400],
  'content-subtle': primitives.neutral[450],
  'content-inverted': primitives.neutral[900],
  'content-brand': primitives.brand[300],
  'input-text': primitives.neutral[100],

  'border-subtle': primitives.neutral[800],
  'border-default': primitives.neutral[700],
  'border-strong': primitives.neutral[600],
  'border-brand': primitives.brand[400],

  'brand-solid': primitives.brand[500],
  // Darker, not lighter, on hover: a lighter blue loses the white label (3.1:1).
  'brand-solid-hover': primitives.brand[600],
  'brand-soft': primitives.brand[950],
  'brand-on-solid': '#ffffff',

  'success-solid': primitives.green[500],
  'success-soft': primitives.green[900],
  'success-content': primitives.green[100],
  // The dark theme's solids are bright: their labels are dark (white on green 500 is 2.3:1).
  'success-on-solid': primitives.neutral[950],
  'warning-solid': primitives.amber[500],
  'warning-soft': primitives.amber[900],
  'warning-content': primitives.amber[100],
  'danger-solid': primitives.red[500],
  'danger-soft': primitives.red[900],
  'danger-content': primitives.red[100],
  'danger-on-solid': primitives.neutral[950],
  'info-solid': primitives.blue[500],
  'info-soft': primitives.blue[900],
  'info-content': primitives.blue[100],

  'accent-solid': primitives.violet[500],
  'accent-soft': primitives.violet[950],
  'accent-content': primitives.violet[200],
  'violet-soft': primitives.violet[900],
  'violet-content': primitives.violet[100],
  'pink-soft': primitives.pink[900],
  'pink-content': primitives.pink[100],
  'teal-soft': primitives.teal[900],
  'teal-content': primitives.teal[100],
  'swatch-green': light['swatch-green'],
  'swatch-yellow': light['swatch-yellow'],
  'swatch-orange': light['swatch-orange'],
  'swatch-red': light['swatch-red'],
  'swatch-pink': light['swatch-pink'],
  'swatch-violet': light['swatch-violet'],
  'swatch-blue': light['swatch-blue'],
  'swatch-teal': light['swatch-teal'],
  'swatch-content': light['swatch-content'],
}

/*
 * The named themes. Each starts from the light or dark set (statuses and tag
 * colours keep their meaning everywhere) and replaces the surfaces, text,
 * borders, brand and accent with its own palette. Gaps in a palette are
 * filled from its own colours: a step lighter or darker for raised and
 * sunken surfaces, the text colour dimmed for muted text, and a brand dark
 * enough (or light enough) to carry its button label.
 */

/** Graphite: charcoal black, greys only; the brand is the light grey itself. */
const graphite: Record<keyof typeof light, string> = {
  ...dark,
  'surface-base': '#121212',
  'surface-raised': '#1a1a1a',
  'surface-sunken': '#0b0b0b',
  'surface-overlay': '#222222',
  'surface-inverted': '#e0e0e0',
  'surface-heading': '#2a2a2a',
  'surface-section': '#202020',
  'surface-subheading': '#1d1d1d',
  'content-default': '#e0e0e0',
  'content-muted': '#b0b0b0',
  'content-subtle': '#9a9a9a',
  'content-inverted': '#121212',
  'content-brand': '#ffffff',
  'input-text': '#e0e0e0',
  'border-subtle': '#2e2e2e',
  'border-default': '#444444',
  'border-strong': '#5c5c5c',
  'border-brand': '#888888',
  'brand-solid': '#e0e0e0',
  'brand-solid-hover': '#ffffff',
  'brand-soft': '#2a2a2a',
  'brand-on-solid': '#121212',
  'accent-solid': '#888888',
  'accent-soft': '#1f1f1f',
  'accent-content': '#c4c4c4',
}

/** Ember: soft black and warm beige, coral to act, gold to mark sections. */
const ember: Record<keyof typeof light, string> = {
  ...dark,
  'surface-base': '#1c1c1c',
  'surface-raised': '#242220',
  'surface-sunken': '#151413',
  'surface-overlay': '#2b2826',
  'surface-inverted': '#f5e8d8',
  'surface-heading': '#35312d',
  'surface-section': '#2b2825',
  'surface-subheading': '#272421',
  'content-default': '#f5e8d8',
  'content-muted': '#cdbfad',
  'content-subtle': '#ad9f8d',
  'content-inverted': '#1c1c1c',
  'content-brand': '#ff6f61',
  'input-text': '#f5e8d8',
  'border-subtle': '#302c29',
  'border-default': '#46413b',
  'border-strong': '#5f5851',
  'border-brand': '#ff6f61',
  'brand-solid': '#ff6f61',
  'brand-solid-hover': '#ff4500',
  'brand-soft': '#3a2420',
  'brand-on-solid': '#1c1c1c',
  'accent-solid': '#daa520',
  'accent-soft': '#332a15',
  'accent-content': '#e8b84a',
}

/** Twilight: slate grey, lavender to act, light cyan and soft pink to mark. */
const twilight: Record<keyof typeof light, string> = {
  ...dark,
  'surface-base': '#2c2c2c',
  'surface-raised': '#333333',
  'surface-sunken': '#252525',
  'surface-overlay': '#3a3a3a',
  'surface-inverted': '#e4e4e4',
  'surface-heading': '#444444',
  'surface-section': '#3b3b3b',
  'surface-subheading': '#373737',
  'content-default': '#e4e4e4',
  'content-muted': '#c2c2c2',
  'content-subtle': '#ababab',
  'content-inverted': '#2c2c2c',
  'content-brand': '#c9b6e4',
  'input-text': '#e4e4e4',
  'border-subtle': '#3e3e3e',
  'border-default': '#505050',
  'border-strong': '#6a6a6a',
  'border-brand': '#b39cd0',
  'brand-solid': '#b39cd0',
  'brand-solid-hover': '#c4b0dd',
  'brand-soft': '#3d3548',
  'brand-on-solid': '#1e1e1e',
  'accent-solid': '#a8dadc',
  'accent-soft': '#2f3b3c',
  'accent-content': '#a8dadc',
  'pink-soft': '#4a3438',
  'pink-content': '#ffc1cc',
  'teal-soft': '#2f3b3c',
  'teal-content': '#a8dadc',
}

/** Olive: near-black green, cream text, sage to act and to mark. */
const olive: Record<keyof typeof light, string> = {
  ...dark,
  'surface-base': '#181c14',
  'surface-raised': '#20251b',
  'surface-sunken': '#12150f',
  'surface-overlay': '#2a2f25',
  'surface-inverted': '#ecdfcc',
  'surface-heading': '#3c3d37',
  'surface-section': '#2c3027',
  'surface-subheading': '#252a21',
  'content-default': '#ecdfcc',
  'content-muted': '#cbbfae',
  'content-subtle': '#aba190',
  'content-inverted': '#181c14',
  'content-brand': '#b8c7b0',
  'input-text': '#ecdfcc',
  'border-subtle': '#2c3027',
  'border-default': '#3c3d37',
  'border-strong': '#697565',
  'border-brand': '#9aaa92',
  'brand-solid': '#9aaa92',
  'brand-solid-hover': '#adbca5',
  'brand-soft': '#2c3527',
  'brand-on-solid': '#181c14',
  'accent-solid': '#697565',
  'accent-soft': '#262c22',
  'accent-content': '#c2cfba',
}

/** Linen: warm off-white and sand, a deep tan to act. */
const linen: Record<keyof typeof light, string> = {
  ...light,
  'surface-base': '#f9f8f6',
  'surface-raised': '#ffffff',
  'surface-sunken': '#efe9e3',
  'surface-overlay': '#ffffff',
  'surface-inverted': '#2b2621',
  'surface-heading': '#e6ded6',
  'surface-section': '#efe9e3',
  'surface-subheading': '#f5f2ee',
  'content-default': '#2b2621',
  'content-muted': '#5e544a',
  'content-subtle': '#6b6054',
  'content-inverted': '#f9f8f6',
  'content-brand': '#6b5844',
  'input-text': '#2b2621',
  'border-subtle': '#e7e0d8',
  'border-default': '#d9cfc7',
  'border-strong': '#c9b59c',
  'border-brand': '#a08463',
  'brand-solid': '#7a6650',
  'brand-solid-hover': '#66543f',
  'brand-soft': '#f1eae1',
  'brand-on-solid': '#ffffff',
  'accent-solid': '#a08463',
  'accent-soft': '#f4eee6',
  'accent-content': '#6e5638',
}

/** Semantic tokens, one entry per theme. Keys match across every theme. */
export const semanticColors = { light, dark, graphite, ember, twilight, olive, linen } as const

/** Whether a theme is light or dark underneath: what `dark:` styles and native controls follow. */
export const themeSchemes: Record<keyof typeof semanticColors, 'light' | 'dark'> = {
  light: 'light',
  dark: 'dark',
  graphite: 'dark',
  ember: 'dark',
  twilight: 'dark',
  olive: 'dark',
  linen: 'light',
}

/** The themes beyond light and dark, in the order a picker shows them. */
export const NAMED_THEMES = ['graphite', 'ember', 'twilight', 'olive', 'linen'] as const

export type ColorToken = keyof typeof light
export type ThemeName = keyof typeof semanticColors
